import React, { useCallback, useEffect, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostFormAction } from '../../Component/PostFormAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader, BTN_PRIMARY } from './shared'
import { mediaUrl, hideBrokenImg } from '../../utils/mediaUrl'

const EMPTY = { gallary_id: '', title: '', file: null }

function GalleryPage() {
  const [rows, setRows] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [preview, setPreview] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const data = await GetAction('get', {}, 'gallary/gallaryList')
    if (data?.status === 'success' && Array.isArray(data.response)) {
      setRows(data.response)
      setError('')
    } else {
      setError(data?.message || 'Failed to load gallery.')
      setRows([])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openNew = () => {
    setForm(EMPTY)
    setPreview('')
    setShowForm(true)
    setMessage(null)
  }

  const openEdit = (row) => {
    setForm({
      gallary_id: row.gallary_id || '',
      title: row.title || '',
      file: null,
    })
    const first = row.gallary_images?.[0]?.path || ''
    setPreview(mediaUrl(first))
    setShowForm(true)
    setMessage(null)
  }

  const onFile = (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setForm((p) => ({ ...p, file }))
    setPreview(URL.createObjectURL(file))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const fd = new FormData()
    if (form.gallary_id) fd.append('gallary_id', form.gallary_id)
    fd.append('title', form.title.trim())
    if (form.file) fd.append('hiddenpath1', form.file)

    const data = await PostFormAction('post', fd, 'gallary/addGallary')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Gallery saved.' })
      setShowForm(false)
      await load()
    } else {
      setMessage({
        error: true,
        text: data?.message || data?.data?.message || 'Failed to save gallery.',
      })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    {
      key: 'thumb',
      label: 'Image',
      render: (row) => {
        const src = mediaUrl(row.gallary_images?.[0]?.path)
        return src ? (
          <img
            src={src}
            alt={row.title || ''}
            className="manage-thumb"
            onError={hideBrokenImg}
          />
        ) : (
          '—'
        )
      },
    },
    { key: 'title', label: 'Title' },
    {
      key: 'count',
      label: 'Images',
      render: (row) => (row.gallary_images || []).length,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => openEdit(row)}
        >
          Edit
        </button>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="gallary">
      <PageHeader
        title="Gallary"
        subtitle="Photo albums shown on the public gallery."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={BTN_PRIMARY}
            onClick={openNew}
          >
            Add Gallery
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable columns={columns} rows={rows} searchKeys={['title']} />
        )}
      </div>

      {showForm ? (
        <div className="manage-card">
          <h2 className="h5 mb-3">
            {form.gallary_id ? 'Edit Gallery' : 'Add Gallery'}
          </h2>
          <form onSubmit={onSubmit}>
            <div className="mb-3">
              <label className="form-label" htmlFor="gal_title">
                Gallery Title
              </label>
              <input
                id="gal_title"
                className="form-control"
                required
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="gal_image">
                Image {form.gallary_id ? '(optional — adds another image)' : '*'}
              </label>
              <input
                id="gal_image"
                type="file"
                accept="image/*"
                className="form-control"
                required={!form.gallary_id}
                onChange={onFile}
              />
              {preview ? (
                <img src={preview} alt="Preview" className="manage-preview mt-2" />
              ) : null}
            </div>
            <div className="d-flex">
              <button
                type="submit"
                className="btn btn-primary mr-2"
                style={BTN_PRIMARY}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </RequireModule>
  )
}

export default GalleryPage
